import React from 'react'
import { I18nComponentType } from 'admin/i18n-types'
import { connect } from 'react-redux'
import moment, { Moment } from 'moment-timezone'
import { mapLanguageToComponentProps } from 'admin/i18n'
import { StyledSelect } from 'components/form-components/styled-select'

import styled from 'styled-components'
import DatePicker from 'components/date-picker-input'
import TimePicker from 'components/time-picker-input'
// @ts-ignore
import TimezonePicker from 'react-timezone'
import { formatTime } from 'utils/date'
import { Body, Label } from 'components/styled/typography'
import { CustomTooltip } from 'admin/components/custom-tooltip'
import { BodyStyle } from 'components/styled/typography/styles'
import { InputStyle } from 'components/form-components/styled-text-input'

export type TimeSelectionValuesType = {
  startDate?: Moment
  endDate?: Moment
  startTime?: Moment
  endTime?: Moment
  timezone: string
  slotLength?: number
}

interface TimeSelectionsProps {
  i18n: I18nComponentType
  slotLength: number
  timeSelectionValues: TimeSelectionValuesType
  setTimeSelectionValues: (values: TimeSelectionValuesType) => void
}

const TimezoneLabel = styled(Label)`
  display: flex;
  align-items: center;
`

const TimeSlotSection = styled.div`
  color: ${({ theme }) => theme.color.bodyText};
  display: flex;
  flex-wrap: wrap;
`

const StyledPicker = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 30%;
  padding: ${({ theme }) => theme.space.s16};
  color: ${({ theme }) => theme.color.grey90};

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex: 1 1 50%;
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    flex: 1 1 100%;
  }

  .react-datepicker-wrapper {
    width: 100%;
    .react-datepicker__input-container {
      width: 100%;

      .date-picker-input {
        width: 100%;
      }
    }
  }

  .time-slot__slot-length {
    margin-bottom: 0;

    ul.dropdown-menu {
      width: 100%;

      li a {
        color: ${({ theme }) => theme.color.bodyText};
        &:hover {
          background-color: ${({ theme }) => theme.color.grey07};
        }
      }
    }

    .dropdown {
      background-color: ${({ theme }) => theme.color.grey07};
      padding: 0 ${({ theme }) => theme.space.s12};
      width: 90%;
    }
  }

  #slot-length-dropdown {
    background: none !important;
    padding: 0;
    width: fit-content;

    &:active,
    &:focus {
      color: ${({ theme }) => theme.color.grey90} !important;
    }
  }
`

const StyledTimezonePicker = styled(TimezonePicker)`
  ${BodyStyle};

  .timezone-picker-textfield input {
    display: inherit;
    font: inherit;
  }

  .timezone-picker-list {
    position: absolute;
    z-index: 11;
    background-color: ${({ theme }) => theme.color.white};
  }

  .timezone-picker-list-item-active {
    background-color: ${({ theme }) => theme.color.grey07};
  }

  .timezone-picker-textfield input {
    ${InputStyle};
    font-weight: 300;
  }
`

const TimeSelections = ({
  i18n,
  setTimeSelectionValues,
  timeSelectionValues,
}: TimeSelectionsProps) => {
  const { startDate, endDate, startTime, endTime, timezone, slotLength } =
    timeSelectionValues
  const renderSlotLengthInput = () => {
    const parseHoursAndMinutesString = (slotLength: number) => {
      if (!slotLength || slotLength === 0) {
        return '-:--'
      }

      if (slotLength < 60) {
        return `0:${slotLength}`
      }

      const hours = Math.floor(slotLength / 60)
      const minutes = slotLength % 60

      return `${hours}:${minutes >= 10 ? minutes : `0${minutes}`}`
    }

    return (
      <div className="time-slot__slot-length">
        <Label>{i18n.modalCronofy.personalInterview.slotLength} (H:MM)</Label>
        <StyledSelect
          // @ts-ignore
          value={slotLength}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
            const eventValue = event && event.target && event.target.value
            const value = eventValue ? parseInt(eventValue, 10) : 60
            setTimeSelectionValues({
              ...timeSelectionValues,
              slotLength: value,
            })
          }}
        >
          {Array(12)
            .fill(null)
            .map((_, i) => {
              if (i === 0) {
                return ''
              }
              const minutes = (i + 1) * 15
              return (
                <option key={minutes} value={minutes}>
                  {parseHoursAndMinutesString(minutes)}
                </option>
              )
            })}
        </StyledSelect>
      </div>
    )
  }

  return (
    <>
      <Body whiteSpace="pre-wrap">
        {i18n.modalCronofy.personalInterview.timeSlot.description}
      </Body>
      <TimeSlotSection className="personal-interview__time-slot">
        <StyledPicker>
          <Label>
            {i18n.modalCronofy.personalInterview.timeSlot.startDate}
          </Label>
          <DatePicker
            placeholderText={
              i18n.modalCronofy.personalInterview.timeSlot.startDatePlaceholder
            }
            timezone={timezone}
            value={startDate}
            minDate={moment().add(1, 'days')}
            onChange={(value: Moment) => {
              if (endDate && value.isAfter(endDate)) {
                setTimeSelectionValues({
                  ...timeSelectionValues,
                  endDate: undefined,
                  startDate: moment.tz(value, timezone),
                })
              } else {
                setTimeSelectionValues({
                  ...timeSelectionValues,
                  startDate: moment.tz(value, timezone),
                })
              }
            }}
          />
        </StyledPicker>
        <StyledPicker>
          <Label>{i18n.modalCronofy.personalInterview.timeSlot.endDate}</Label>
          <DatePicker
            placeholderText={`${i18n.modalCronofy.personalInterview.timeSlot.endDatePlaceholder}*`}
            timezone={timezone}
            value={endDate}
            minDate={startDate ? moment(startDate) : moment().add(1, 'days')}
            maxDate={
              startDate
                ? moment(startDate).add(34, 'days')
                : moment().add(34, 'days')
            }
            onChange={(value: Moment) => {
              setTimeSelectionValues({
                ...timeSelectionValues,
                endDate: moment.tz(value, timezone),
              })
            }}
          />
          {!endDate && (
            <Body color="warn110">
              *{i18n.modalCronofy.personalInterview.timeSlot.endDateWarning}
            </Body>
          )}
        </StyledPicker>
        <StyledPicker>
          <TimezoneLabel>
            {i18n.modalCronofy.personalInterview.timeSlot.timezone}
            <CustomTooltip id="timezone-help" modalPopover>
              {i18n.modalCronofy.personalInterview.timeSlot.timezoneHelp}
            </CustomTooltip>
          </TimezoneLabel>
          <StyledTimezonePicker
            onChange={(value: string) => {
              setTimeSelectionValues({
                ...timeSelectionValues,
                timezone: value,
                startDate: startDate && moment.tz(startDate, value),
                endDate: endDate && moment.tz(endDate, value),
                startTime: startTime && moment.tz(startTime, value),
                endTime: endTime && moment.tz(endTime, value),
              })
            }}
            value={timezone}
          />
        </StyledPicker>
        <StyledPicker>
          <Label>
            {i18n.modalCronofy.personalInterview.timeSlot.startTime}
          </Label>
          <TimePicker
            time={formatTime(startTime, timezone)}
            timezone={timezone}
            value={startTime}
            onChange={(value: Moment) =>
              setTimeSelectionValues({
                ...timeSelectionValues,
                startTime: moment.tz(value, timezone),
              })
            }
            defaultTime=""
            resetTimePicker={false}
          />
        </StyledPicker>
        <StyledPicker>
          <Label>{i18n.modalCronofy.personalInterview.timeSlot.endTime}</Label>
          <TimePicker
            time={formatTime(endTime, timezone)}
            timezone={timezone}
            value={endTime}
            onChange={(value: Moment) =>
              setTimeSelectionValues({
                ...timeSelectionValues,
                endTime: moment.tz(value, timezone),
              })
            }
            defaultTime=""
            resetTimePicker={false}
          />
        </StyledPicker>
        <StyledPicker>{renderSlotLengthInput()}</StyledPicker>
      </TimeSlotSection>
    </>
  )
}

export default connect(mapLanguageToComponentProps())(TimeSelections)
